import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  IconButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  useDisclosure,
  Text,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import CountryFlag from "react-country-flag";

import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import { useTranslation } from "../LanguageTranslation/TranslationContext";
import Translate from "../LanguageTranslation/Translate";
import HamburgerButton from "./HamburgerButton";
import axiosInstance from "../../helpers/axiosInstance";
import MobileDrawerMenu from "../Sidebar/MobileDrawerMenu";

const availableLanguages = [
  { code: "en", label: "English (US)" },
  { code: "en-GB", label: "English (UK)" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "nl", label: "Nederlands" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "no", label: "Norsk" },
  { code: "sv", label: "Svenska" },
  { code: "fi", label: "Suomi" },
  { code: "da", label: "Dansk" },
  { code: "is", label: "Íslenska" },

  // { code: "cs", label: "Čeština" },
  // { code: "zh", label: "中文 (简体)" },
  // { code: "ja", label: "日本語" },
  // { code: "ko", label: "한국어" },
  // { code: "id", label: "Bahasa Indonesia" },
  // { code: "ms", label: "Bahasa Malaysia" },
  // { code: "tl", label: "Filipino" },
  // { code: "vi", label: "Tiếng Việt" },
  // { code: "th", label: "ไทย" },
];

// Mapping language codes to country codes for flags
const languageToCountryMap = {
  en: "US",
  "en-GB": "GB",
  cs: "CZ",
  da: "DK",
  nl: "NL",
  fi: "FI",
  fr: "FR",
  de: "DE",
  it: "IT",
  is: "IS",
  no: "NO",
  es: "ES",
  sv: "SE",
  pt: "PT",
  zh: "CN",
  ja: "JP",
  ko: "KR",
  id: "ID",
  ms: "MY",
  tl: "PH",
  vi: "VN",
  th: "TH",
};

const availableCurrencies = [
  { code: "USD", label: "USD - US Dollar ($)" },
  { code: "GBP", label: "GBP - British Pound (£)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "NOK", label: "NOK - Norwegian krone (kr)" },
  { code: "SEK", label: "SEK - Swedish krona (kr)" },
  { code: "EUR", label: "EUR - Euro (€)" },
  { code: "DKK", label: "DKK - Danish krone (kr)" },
  { code: "ISK", label: "ISK - Icelandic króna (kr)" },
  { code: "CHF", label: "CHF - Swiss Franc (Fr.)" },

  // { code: "CZK", label: "CZK - Czech Koruna (Kč)" },

  // { code: "JPY", label: "JPY - Japanese Yen (¥)" },
  // { code: "CNY", label: "CNY - Chinese Yuan (¥)" },
  // { code: "KRW", label: "KRW - South Korean Won (₩)" },
  // { code: "IDR", label: "IDR - Indonesian Rupiah (Rp)" },
  // { code: "MYR", label: "MYR - Malaysian Ringgit (RM)" },
  // { code: "PHP", label: "PHP - Philippine Peso (₱)" },
  // { code: "VND", label: "VND - Vietnamese Dong (₫)" },
  // { code: "THB", label: "THB - Thai Baht (฿)" },
];

const uniqueCurrencies = Array.from(
  new Map(availableCurrencies.map((item) => [item.code, item])).values()
);

const languageToCurrencyMap = {
  nl: "EUR",
  en: "USD",
  "en-GB": "GBP",
  cs: "CZK",
  da: "DKK",
  no: "NOK",
  sv: "SEK",
  ja: "JPY",
  zh: "CNY",
  ko: "KRW",
  id: "IDR",
  ms: "MYR",
  tl: "PHP",
  vi: "VND",
  th: "THB",
  fi: "EUR",
  fr: "EUR",
  de: "EUR",
  it: "EUR",
  is: "ISK",
  es: "EUR",
  pt: "EUR",
};

const SettingsMenu = ({
  languageCodeColor = "black",
  showMobileDrawer = true,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { language, setLanguage } = useTranslation();
  const { currency, setCurrency } = useCurrency();

  const updateLanguage = async (languageToSet) => {
    try {
      const response = await axiosInstance.put(`/user_settings/language/`, {
        language: languageToSet,
      });
      // console.log("Language setting saved:", response.data.data);
    } catch (error) {
      console.error("Error updating user language setting:", error);
    }

    setLanguage(languageToSet);
    updateCurrency(languageToCurrencyMap[languageToSet]);
  };

  const updateCurrency = async (currencyToSet) => {
    try {
      const response = await axiosInstance.put(`/user_settings/currency/`, {
        currency: currencyToSet,
      });
      // console.log("Currency setting saved:", response.data.data);
    } catch (error) {
      console.error("Error updating user currency setting:", error);
    }

    setCurrency(currencyToSet);
  };

  const handleCurrencyChange = (event) => {
    const selectedCurrency = event.target.value;
    updateCurrency(selectedCurrency);
    // updateLanguage(currencyToLanguageMap[selectedCurrency]);
  };

  return (
    <Box display="flex">
      <Flex align="center" onClick={onOpen} cursor="pointer">
        <IconButton
          icon={
            <CountryFlag
              countryCode={languageToCountryMap[language]}
              svg
              style={{
                width: "1.5em",
                height: "auto",
                marginLeft: "0.25em",
                boxShadow:
                  "-4px 0 4px -2px rgba(0,0,0,0.1), 4px 0 4px -2px rgba(0,0,0,0.1)",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
              }}
            />
          }
          aria-label="Open settings"
          bg="none"
          _hover="none"
          _active="none"
        />
        <Text
          textTransform="uppercase"
          fontWeight="bold"
          fontSize="sm"
          color={languageCodeColor}
          mb={0}
          ml={2}
        >
          {language}
        </Text>
        {/* <Box
          height="1.5em"
          borderLeft="1px solid"
          borderColor="gray.300"
          mx={2}
        />
        <Text fontWeight="bold" fontSize="sm" color={languageCodeColor} mb={0}>
          {currency} {currencySymbols[currency]}
        </Text> */}
        <ChevronDownIcon
          fontSize="xl"
          color={languageCodeColor}
          ml={2}
          mr={2}
        />
      </Flex>
      {showMobileDrawer && <MobileDrawerMenu />}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        // isCentered
        size={{ base: "xs", md: "md" }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Translate text="Select Preferences" />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Language Selector */}
            <Box mb={4}>
              <Menu w="100%">
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  width="100%"
                  textAlign="left"
                  px={4}
                  py={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  bg="white"
                  _hover={{ bg: "gray.100" }}
                  _expanded={{ bg: "gray.100" }}
                  _focus={{ boxShadow: "outline" }}
                  fontWeight="normal"
                >
                  <CountryFlag
                    countryCode={languageToCountryMap[language]}
                    svg
                    style={{
                      width: "1em",
                      height: "1em",
                      marginRight: "0.5em",
                    }}
                  />
                  {availableLanguages.find((lang) => lang.code === language)
                    ?.label || "Select Language"}
                </MenuButton>
                <MenuList maxHeight="40vh" overflowY="auto">
                  {availableLanguages.map(({ code, label }) => (
                    <MenuItem key={code} onClick={() => updateLanguage(code)}>
                      <CountryFlag
                        countryCode={languageToCountryMap[code]}
                        svg
                        style={{
                          width: "1em",
                          height: "1em",
                          marginRight: "0.5em",
                        }}
                      />
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
            {/* Currency Selector */}
            <Box mb={4}>
              {/* <Select value={currency} onChange={handleCurrencyChange}>
                {availableCurrencies.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </Select> */}
              <Select value={currency} onChange={handleCurrencyChange}>
                {uniqueCurrencies.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </Select>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button bg="black" color="white" _hover="none" onClick={onClose}>
              <Translate text="Close" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SettingsMenu;
